import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = { class: "px-5 py-3 bg-white border border-gray-200 shadow rounded-lg" }
const _hoisted_2 = { class: "text-gray-900 text-xl font-bold mb-2" }
const _hoisted_3 = { class: "block text-sm leading-6 text-gray-900" }
const _hoisted_4 = ["value"]
const _hoisted_5 = { class: "px-5 py-3 bg-white border flex flex-col gap-2 border-gray-200 shadow sm:rounded-lg" }
const _hoisted_6 = { class: "text-gray-900 text-xl font-bold" }
const _hoisted_7 = { class: "relative rounded-md bg-white py-1.5 pl-3 pr-1.5 shadow-sm ring-1 ring-inset ring-gray-300 w-full group/filter-box" }
const _hoisted_8 = { class: "flex items-center justify-between" }
const _hoisted_9 = { class: "font-normal mb-1" }
const _hoisted_10 = { class: "text-sm flex items-center" }
const _hoisted_11 = { class: "mr-3 text-gray-400" }
const _hoisted_12 = {
  key: 0,
  class: "flex justify-between"
}
const _hoisted_13 = { class: "text-gray-500 max-w-[160px] overflow-hidden whitespace-nowrap text-ellipsis mr-1" }
const _hoisted_14 = { class: "text-gray-500" }
const _hoisted_15 = { key: 1 }
const _hoisted_16 = { class: "px-5 py-3 bg-white border border-gray-200 shadow sm:rounded-lg" }
const _hoisted_17 = { class: "text-gray-900 text-xl font-bold mb-2" }

import { XMarkIcon } from "@heroicons/vue/24/outline";
import { Component as VueComponent, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ProcessLocationFilters from "@/components/process_filters/ProcessLocationFilter.vue";
import { HIERARCHY_TAGS_META } from "@/constants/hierarchyTags";
import { HierarchyTagStore, HierarchyType } from "@/types/HierarchyTag";
import { ResourceGroup } from "@/types/Process";
import {
  Report,
  ReportProcessFilter,
  ReportLocationFilter,
  ReportFilter,
  ReportConfig,
  ReportPlotType,
} from "@/types/Report";
import CustomFilters from "./CustomFilters.vue";
import CycleTimesConfig from "./CycleTimesConfig.vue";
import MilestoneConfig from "./MilestoneConfig.vue";
import UnitValuesConfig from "./UnitValuesConfig.vue";
import WorkingHourCurveConfig from "./WorkingHourCurveConfig.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'Form',
  props: {
    report: {},
    hierarchyTags: {}
  },
  emits: ["change"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

const props = __props;
const emit = __emit;

const configComponents: Record<ReportPlotType, VueComponent> = {
  cycle_times: CycleTimesConfig,
  working_hour_curve: WorkingHourCurveConfig,
  milestone: MilestoneConfig,
  unit_values: UnitValuesConfig,
};

const selectedProcesses = ref<ReportProcessFilter["value"]>({
  mode: "component",
  components: [],
  single_processes: [],
});
const availableHierarchyTags = ref<Exclude<HierarchyType, "component">[]>([]);
const selectedSectionMasks = ref({
  building: new Array<string>(),
  level: new Array<string>(),
  section: new Array<string>(),
});

const tagGroups = ref<ResourceGroup[]>([]);

const getAvailableHierarchyTags = () =>
  Array.from(new Set(props.hierarchyTags.map((tag) => tag.type)))
    .filter((tag) => tag !== "component")
    .sort((a, b) => HIERARCHY_TAGS_META[a].order - HIERARCHY_TAGS_META[b].order) as Exclude<
    HierarchyType,
    "component"
  >[];

const remapHierarchyTagsToTagPickerGroups = () =>
  props.hierarchyTags
    .reduce((acc, tag) => {
      const isMilestonePlot = props.report?.plot?.type === "milestone";

      if (tag.type === "component" || (isMilestonePlot && tag.type === "level")) {
        return acc;
      }

      const group = acc.find((item) => item.field === tag.type);

      if (group) {
        group.options.push({ name: tag.name, value: tag._id });
      } else {
        acc.push({
          name: t(`analytics.reports.${tag.type}`),
          field: tag.type,
          options: [{ name: tag.name, value: tag._id }],
        });
      }

      return acc;
    }, [] as ResourceGroup[])
    .sort((a, b) => HIERARCHY_TAGS_META[a.field].order - HIERARCHY_TAGS_META[b.field].order);

watch(
  () => props.report,
  (report) => {
    const reportProcessFilter = (report.filters.find((filter) => {
      return filter.type === "processes";
    }) || []) as ReportProcessFilter;
    const reportBuildingFilter = report.filters.find((filter) => {
      return filter.type === "building";
    }) as ReportLocationFilter;
    const reportLevelFilter = report.filters.find((filter) => {
      return filter.type === "level";
    }) as ReportLocationFilter;
    const reportSectionFilter = report.filters.find((filter) => {
      return filter.type === "section";
    }) as ReportLocationFilter;

    selectedProcesses.value = reportProcessFilter?.value;
    availableHierarchyTags.value = getAvailableHierarchyTags();
    tagGroups.value = remapHierarchyTagsToTagPickerGroups();

    selectedSectionMasks.value = {
      building: reportBuildingFilter?.value || [],
      level: reportLevelFilter?.value || [],
      section: reportSectionFilter?.value || [],
    };
  },
  { immediate: true },
);

const populateSectionMask = (id: string) => props.hierarchyTags.find((tag) => tag._id === id);

const changeSectionMaskFilters = () => {
  const updatedReport: Partial<Report> = {
    filters: [
      ...props.report.filters.filter(
        (reportFilter) => !["building", "level", "section"].includes(reportFilter.type),
      ),
      { type: "building", value: selectedSectionMasks.value.building },
      { type: "level", value: selectedSectionMasks.value.level },
      { type: "section", value: selectedSectionMasks.value.section },
    ],
  };
  emit("change", updatedReport);
};

const handleLocationFilterRemoveFilter = () => {
  selectedSectionMasks.value = {
    building: [],
    level: [],
    section: [],
  };
  changeSectionMaskFilters();
};

const handleSectionMasksChange = (sectionMasks: typeof selectedSectionMasks.value) => {
  selectedSectionMasks.value = sectionMasks;
  changeSectionMaskFilters();
};

const handleConfigChange = (config: ReportConfig) => {
  const updatedReport: Partial<Report> = {
    plot: {
      ...props.report.plot,
      config,
    },
  };
  emit("change", updatedReport);
};

const handleCustomFilterChange = (customFilters: ReportFilter[]) => {
  const updatedReport: Partial<Report> = {
    filters: [
      ...props.report.filters.filter((reportFilter) =>
        ["building", "level", "section"].includes(reportFilter.type),
      ),
      ...customFilters,
    ],
  };
  emit("change", updatedReport);
};

const handleReportNameChange = (event: Event) => {
  const name = (event.target as HTMLInputElement).value;
  const updatedReport: Partial<Report> = {
    name,
  };
  emit("change", updatedReport);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['flex flex-col gap-4', !_ctx.report && 'invisible'])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)("analytics.reports.general")), 1),
      _createElementVNode("label", null, [
        _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)("analytics.reports.name")), 1),
        _createElementVNode("input", {
          value: _ctx.report.name,
          onInput: handleReportNameChange,
          type: "text",
          autocomplete: "one-time-code",
          class: "mb-4 outline-none block w-full rounded-md border-0 py-1.5 pl-3 ring-1 ring-inset focus:ring-2 focus:ring-inset text-sm sm:leading-6 text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-yellow-300 pr-10,"
        }, null, 40, _hoisted_4)
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(t)("analytics.reports.filters")), 1),
      _createElementVNode("div", {
        class: _normalizeClass(["flex gap-2", _ctx.report?.plot?.type === 'unit_values' ? 'flex-col-reverse' : 'flex-col'])
      }, [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(t)("analytics.reports.location")), 1),
            _createElementVNode("div", {
              class: "cursor-pointer p-1 invisible group-hover/filter-box:visible rounded-md hover:bg-red-200 active:bg-red-300 transition-colors duration-100",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (handleLocationFilterRemoveFilter()))
            }, [
              _createVNode(_unref(XMarkIcon), {
                class: "h-4 w-4 select-none",
                "aria-hidden": "true"
              })
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)("analytics.reports.is")), 1),
            _createVNode(ProcessLocationFilters, {
              class: "lg:w-72 mt-2 lg:mt-0 flex-1",
              "onUpdate:selected": handleSectionMasksChange,
              selected: selectedSectionMasks.value,
              groups: tagGroups.value,
              changeOnSubmit: "",
              popoverClass: "translate-x-0 lg:translate-x-0 xl:-translate-x-8 md:right-auto mt-3",
              gridBoxClass: `${tagGroups.value.length >= 2 && 'xl:!grid-cols-2'} ${
                tagGroups.value.length >= 3 && '2xl:!grid-cols-3'
              }`
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", {
                  class: _normalizeClass([
                  'flex py-0.5 px-4 border rounded-full max-w-60 cursor-pointer truncate',
                  Object.values(selectedSectionMasks.value).some((mask) => mask.length)
                    ? 'border-gray-300'
                    : 'border-yellow-400',
                ])
                }, [
                  (Object.values(selectedSectionMasks.value).some((mask) => mask.length))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        _createElementVNode("p", _hoisted_13, _toDisplayString(Object.values(selectedSectionMasks.value)
                        .flatMap((masks) => masks.map((item) => populateSectionMask(item)?.name))
                        .join(", ")), 1),
                        _createElementVNode("span", _hoisted_14, " (" + _toDisplayString(Object.values(selectedSectionMasks.value).reduce(
                        (acc, mask) => acc + mask.length,
                        0,
                      )) + ") ", 1)
                      ]))
                    : (_openBlock(), _createElementBlock("p", _hoisted_15, "..." + _toDisplayString(_unref(t)("analytics.reports.select_values")), 1))
                ], 2)
              ]),
              _: 1
            }, 8, ["selected", "groups", "gridBoxClass"])
          ])
        ]),
        (_ctx.report)
          ? (_openBlock(), _createBlock(CustomFilters, {
              key: 0,
              report: _ctx.report,
              hierarchyTags: _ctx.hierarchyTags,
              onUpdate: handleCustomFilterChange
            }, null, 8, ["report", "hierarchyTags"]))
          : _createCommentVNode("", true)
      ], 2)
    ]),
    _createElementVNode("div", _hoisted_16, [
      _createElementVNode("div", _hoisted_17, _toDisplayString(_unref(t)("analytics.reports.plot_config")), 1),
      (_openBlock(), _createBlock(_resolveDynamicComponent(configComponents[_ctx.report.plot.type]), {
        config: _ctx.report.plot.config,
        filters: _ctx.report.filters,
        hierarchyTags: props.hierarchyTags,
        onConfigChange: handleConfigChange
      }, null, 40, ["config", "filters", "hierarchyTags"]))
    ])
  ], 2))
}
}

})